import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, MessageCircle } from "lucide-react";
import { SITE, NAV } from "../../lib/config";
import { api } from "../../lib/api";
import { useQuote } from "./Header";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const openQuote = useQuote();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.newsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-[#0A1C0E] text-white overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-20 pb-10 relative z-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="inline-flex bg-white rounded-xl p-3 mb-5">
              <img src="/logo-trim.png" alt="Chennai Packs" className="h-10 w-auto" />
            </div>
            <p className="text-white/60 max-w-sm leading-relaxed mb-6">
              Premium food packaging for restaurants, cloud kitchens, hotels, caterers and
              wholesalers across India. Quality, scale and sustainability — delivered.
            </p>
            <form onSubmit={subscribe} className="max-w-sm" data-testid="newsletter-form">
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-[#FDCD00]">
                Newsletter
              </label>
              <div className="mt-2 flex items-center gap-2 bg-white/5 border border-white/15 rounded-full p-1.5 pl-4 focus-within:border-[#4CAF50] transition-colors">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  data-testid="newsletter-input"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="grid place-items-center w-9 h-9 rounded-full bg-[#4CAF50] hover:bg-[#3d9142] transition-colors disabled:opacity-50"
                >
                  <Send size={16} />
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-head font-semibold mb-4 text-[#4CAF50]">Explore</h4>
            <ul className="space-y-2.5">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className="text-white/60 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => openQuote()}
                  className="text-white/60 hover:text-white transition-colors text-sm bg-transparent border-0 p-0 cursor-pointer text-left"
                >
                  Request a Quote
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-head font-semibold mb-4 text-[#4CAF50]">Get in touch</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin size={18} className="text-[#FDCD00] shrink-0 mt-0.5" />
                <span className="text-white/70">{SITE.addressLines.join(" ")}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone size={18} className="text-[#FDCD00] shrink-0" />
                <a href={`tel:${SITE.phoneRaw}`} className="text-white/70 hover:text-white">
                  {SITE.phone}
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <MessageCircle size={18} className="text-[#FDCD00] shrink-0" />
                <a
                  href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for your packaging products.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white"
                >
                  +91 8870705520
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail size={18} className="text-[#FDCD00] shrink-0" />
                <a href={`mailto:${SITE.email}`} className="text-white/70 hover:text-white">
                  {SITE.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/40">
          <p>© {new Date().getFullYear()} Jes Mes Enterprises. All rights reserved.</p>
          <p>
            Premium B2B Food Packaging •{" "}
            <Link to="/admin/login" className="hover:text-white/70">
              Admin
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
